import React from "react";

import {
    AppState,
    AppDispatch
} from "interfaces/context/appContextInterfaces";
import { companyReducer } from "reducers/companyReducer";
import { userReducer } from "reducers/userReducer";
import { useCombinedReducers } from "utils/reducers";
import { ORMSingleCompany } from "interfaces/api/apiInterfaces";

const defaultAppState: AppState = {
    user: {
        logged_in: false,
        view_bookings: false,
        company: null,
        location: null,
        cropped_name: "",
        email: "",
        favorite_posts: [],
        name: "",
        id: null,
        isInitialized: false
    },
    company: {} as ORMSingleCompany
};

const AppStateContext = React.createContext<AppState | undefined>(defaultAppState);
const AppDispatchContext = React.createContext<AppDispatch | undefined>(undefined);
/**
 * App Context Provider
 *
 * How to use:
 *
 * Wrap the provider around the components that need access to the context data.
 * ```
 * <AppProvider>
 *   <Component />
 * </AppProvider>
 * ```
 * Access the state in <Component />
 * ```
 * const state = useAppState();
 * ```
 * Call dispatch to update the context state
 * ```
 * const dispatch = useAppDispatch();
 * dispatch({ type: ACTION_TYPE, payload: ACTION_PAYLOAD });
 * ```
 */
const AppProvider: React.FC = (props: React.PropsWithChildren<AppState>) => {
    const { company, children } = props;
    const [state, dispatch] = useCombinedReducers({
        company: React.useReducer(companyReducer, company ? { ...company } : defaultAppState.company),
        user: React.useReducer(userReducer, defaultAppState.user),
    });

    return (
        <AppStateContext.Provider value={state as AppState}>
            <AppDispatchContext.Provider value={dispatch as AppDispatch}>
                {children}
            </AppDispatchContext.Provider>
        </AppStateContext.Provider>
    );
};

const useAppState = () => {
    const context = React.useContext(AppStateContext);
    if (context === undefined) {
        throw new Error("useAppState must be used within the AppProvider");
    }
    return context;
};

const useAppDispatch = () => {
    const context = React.useContext(AppDispatchContext);
    if (context === undefined) {
        throw new Error("useAppDispatch must be used within the AppProvider");
    }
    return context;
};

AppProvider.displayName = "AppProvider";
useAppState.displayName = "useAppState";
useAppDispatch.displayName = "useAppDispatch";

export { AppProvider, useAppState, useAppDispatch };
